import Pubnub from 'pubnub';

let connection;
let connectedChannels = {};
let storedPublishKey;

const messageSubscriptons = new Set();
const presenceSubscriptions = new Set();
const statusSubscriptions = new Set();

const handlePubnubError = err => {
    // console.log(`pubnub error: ${err}`);
    // is there anything we want to do with these errors?
    // we probably dont just want to swallow them
    throw err;
};

const realHandler = {
    message: function() {
        messageSubscriptons.forEach(handler => handler.apply(undefined, arguments));
    },
    presence: function() {
        presenceSubscriptions.forEach(handler => handler.apply(undefined, arguments));
    },
    status: function() {
        statusSubscriptions.forEach(handler => handler.apply(undefined, arguments));
    },
    // status: statusEvent => {
    //     switch (statusEvent.category) {
    //         case 'PNDisconnectedCategory':
    //         case 'PNNetworkDownCategory':
    //             connect(); // reconnect
    //             break;
    //     }
    // },
};

export const connect = async (publishKey, pubnubId, subscribeKey) => {
    if (connection && (!publishKey || publishKey === storedPublishKey)) {
        return connection;
    }

    if (connection && publishKey && publishKey !== storedPublishKey) {
        await connection.then(({ pubnub }) => {
            pubnub.removeListener(realHandler);
            pubnub.unsubscribeAll();
            pubnub.stop();
        });
    }

    connection = new Promise((resolve, reject) => {
        const options = {
            publishKey: publishKey ? publishKey : '',
            restore: true,
            subscribeKey,
            uuid: pubnubId,
        };

        storedPublishKey = options.publishKey;

        const pubnub = new Pubnub(options);

        const initialHandler = {
            status: statusEvent => {
                switch (statusEvent.category) {
                    case 'PNConnectedCategory':
                    case 'PNNetworkUpCategory':
                        resolve(pubnub);
                        break;
                    case 'PNDisconnectedCategory':
                    case 'PNNetworkDownCategory':
                        reject(new Error('Received a network-down message'));
                        break;
                    default:
                        return;
                }

                pubnub.removeListener(initialHandler);

                pubnub.addListener(realHandler);
            },
        };

        pubnub.addListener(initialHandler);

        return handshake(pubnub)
            .then(() => resolve({ pubnub, uuid: pubnubId }))
            .catch(reject);
    });

    return connection;
};

export const disconnect = () => {
    connect()
        .then(({ pubnub }) => {
            let connectedCount = 0;
            Object.keys(connectedChannels).forEach(k => (connectedCount += connectedChannels[k].count));

            if (!connectedCount) {
                pubnub.removeListener(realHandler);
                pubnub.unsubscribeAll();
                pubnub.stop();
                messageSubscriptons.clear();
                presenceSubscriptions.clear();
                statusSubscriptions.clear();
                connectedChannels = {};
                connection = null;
            }
        })
        .catch(handlePubnubError);
};

const handshake = pubnub =>
    new Promise((resolve, reject) => {
        pubnub.time(status => {
            if (status.error) {
                reject(new Error(`Pubnub service failed to respond to time request: ${status}`));
            } else {
                resolve(pubnub);
            }
        });
    });

export const subscribe = (channel, presenceHandler, messageHandler, statusHandler, presenceOnly = false) => {
    const data = connectedChannels[channel] || { count: 0 };
    connectedChannels[channel] = data;

    const initial = data.count;

    data.count = data.count > 0 ? (data.count += 1) : (data.count = 1);

    const shouldSubscribe = initial === 0 && data.count === 1;

    if (shouldSubscribe) {
        messageSubscriptons.add(messageHandler);
        presenceSubscriptions.add(presenceHandler);
        statusSubscriptions.add(statusHandler);

        data.messageHandler = messageHandler;
        data.presenceHandler = presenceHandler;
        data.statusHandler = statusHandler;

        if (presenceOnly) {
            connect()
                .then(({ pubnub }) => {
                    pubnub.subscribe({
                        channels: [`${channel}-pnpres`],
                    });
                })
                .catch(handlePubnubError);
        } else {
            connect()
                .then(({ pubnub }) => {
                    pubnub.subscribe({
                        channels: [channel],
                        withPresence: true,
                    });
                })
                .catch(handlePubnubError);
        }
    }

    return {
        unsubscribe: () => {
            const data = connectedChannels[channel];
            if (data.count > 0) {
                data.count -= 1;
            }
            const shouldUnsubscribe = data.count === 0;
            if (shouldUnsubscribe) {
                messageSubscriptons.delete(data.messageHandler);
                presenceSubscriptions.delete(data.presenceHandler);
                statusSubscriptions.delete(data.statusHandler);

                if (presenceOnly) {
                    return connect()
                        .then(({ pubnub }) => {
                            pubnub.unsubscribe({ channels: [`${channel}-pnpres`] });
                        })
                        .catch(handlePubnubError);
                } else {
                    return connect()
                        .then(({ pubnub }) => {
                            pubnub.unsubscribe({ channels: [channel] });
                        })
                        .catch(handlePubnubError);
                }
            }
        },
    };
};

export const hereNow = channel =>
    new Promise((resolve, reject) => {
        connect()
            .then(({ pubnub }) =>
                pubnub.hereNow(
                    {
                        channels: [channel],
                        includeState: false,
                        includeUUIDs: false,
                    },
                    (status, response) => {
                        if (status.error) {
                            reject(status.category);
                        } else {
                            resolve(response);
                        }
                    },
                ),
            )
            .catch(handlePubnubError);
    });

export const publish = (channel, message) =>
    new Promise((resolve, reject) => {
        connect()
            .then(({ pubnub }) =>
                pubnub.publish(
                    {
                        channel,
                        message,
                    },
                    status => {
                        if (status.error) {
                            reject(status.category);
                        } else {
                            resolve();
                        }
                    },
                ),
            )
            .catch(handlePubnubError);
    });

export const setFilterExpression = filterExpression =>
    new Promise(resolve => {
        connect()
            .then(({ pubnub }) => {
                pubnub.setFilterExpression(filterExpression);
                resolve();
            })
            .catch(handlePubnubError);
    });

export const keepalive = () =>
    new Promise((resolve, reject) => {
        connect()
            .then(({ pubnub }) =>
                handshake(pubnub)
                    .then(resolve())
                    .catch(err => reject(err)),
            )
            .catch(err => reject(err));
    });
